import { forwardRef, CSSProperties } from 'react'

import { Breakpoint, useCheckBreakpoint } from '~/utils/useBreakpoint'
import { range } from '~/utils/helpers'

import { selectVideoTopicColor } from '~/bootcamp/slices/vcc-data.slice'
import { useAppSelector } from '~/redux/hooks'
import { CategoryColorClasses } from '~/bootcamp/constants/bootcamp.const'

import { isAllowedValue } from '~/bootcamp/Activities/utils/activities.utils'

import MultiInput from '~/bootcamp/Activities/Multiplication/components/MultiInput'
import useInAppKeyboardLayout from '~/in-app-keyboard/hooks/useInAppKeyboard'

import keyboardStyles from '~/in-app-keyboard/styles/inAppKeyboard.module.css'

export interface InputSingleDigitColumnsAnswer {
  [key: string]: string | number
}

interface InputProps {
  className?: string
  isError?: boolean
  onChange: (value: string, name: string) => void
  style?: CSSProperties
  values: InputSingleDigitColumnsAnswer
  ref?: boolean
  id?: string
  autoFocus?: boolean
  onEnter?: () => void
}

// eslint-disable-next-line react/display-name
const InputSingleDigitColumns = forwardRef<HTMLInputElement | null, InputProps>((props, ref) => {
  const { className, isError, onChange, onEnter, style, values, id, autoFocus } = props
  const entries = Object.entries(values)
  const lastIndex = entries.length - 1

  const colorInfo: CategoryColorClasses = useAppSelector(selectVideoTopicColor)

  const isMobile = useCheckBreakpoint({ lt: Breakpoint.SM })
  const fontSize = isMobile ? 18 : 24
  const keyboardLayout = useInAppKeyboardLayout(null)

  const containerStyle = {
    backgroundBlendMode: 'soft-light, normal',
    fontSize: `${fontSize}px`,
    height: `${fontSize * 2}px`,
  }

  const handleOnChange = (inputVal: string, inputName: string): void => {
    if (isAllowedValue(inputVal) && inputVal.length <= 1) {
      onChange(inputVal, inputName)
    }
  }

  return (
    <div
      className={`my-1 w-max rounded-small ${
        isError ? ' bg-red' : `outline-0 border-2 ${colorInfo.borderClass} `
      } ${className}`}
      style={{ ...style, ...containerStyle }}
    >
      <div className="relative shadow-input w-max" style={{ margin: '-2px', height: 'calc(100% + 4px)' }}>
        <div className="absolute overflow-hidden inset-0 flex items-center justify-evenly pointer-events-none">
          {range(Object.keys(values).length - 1).map((key) => (
            <div
              className={`h-[80%] w-px ${colorInfo.bgClass} bg-opacity-50`}
              key={`inputSingleDigitColumns-values-${key}`}
            />
          ))}
        </div>
        {entries.map(([key, value], index) => (
          <MultiInput
            autoFocus={lastIndex === index && autoFocus}
            className={
              'inline-block h-full w-8 sm:w-10 bg-transparent focus:outline-0  focus:rounded-small focus:border-2 focus:border-black-light no-input-arrows ' +
              'text-center  font-semibold placeholder-purple-light ' +
              `${isError ? 'text-white' : 'text-purple hover:bg-purple-light-deep'} ` +
              `${
                keyboardLayout
                  ? `${keyboardStyles['input-multiplication-math-field']} ${keyboardStyles['input-common-math-field']}`
                  : ''
              }`
            }
            id={key}
            isError={isError}
            key={id ? `${id}-${key}` : key}
            name={key}
            onChange={handleOnChange}
            onEnter={onEnter}
            ref={ref}
            value={String(value)}
          />
        ))}
      </div>
    </div>
  )
})

export default InputSingleDigitColumns
